import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import { useAuth } from '../contexts/AuthContext';
import { Users, Edit, Trash2, Shield, Activity, RefreshCw, LogOut } from 'lucide-react';
import { formatDateTime, delay } from '../utils/helpers';
import toast from 'react-hot-toast';

const AdminPage = () => {
  const { mockData, user, switchUser, restoreAdmin } = useAuth();
  const navigate = useNavigate();
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [isSwitching, setIsSwitching] = useState(false);
  const [activeTab, setActiveTab] = useState('usuarios');
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);

  const handleSwitchUser = async (targetUser) => {
    if (!targetUser) return;

    setIsSwitching(true);
    try {
      await delay(500);
      await switchUser(targetUser.email);
      toast.success(`Cambiado a rol: ${targetUser.role.replace('_', ' ')}`);
      setShowSwitchModal(false);

      // Navegar al dashboard correspondiente
      if (targetUser.role === 'cliente') {
        navigate('/cliente/garantia');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Error al cambiar de rol');
    } finally {
      setIsSwitching(false);
    }
  };



  const handleRestoreAdmin = async () => {
    setIsSwitching(true);
    try {
      await restoreAdmin();
      toast.success('Restaurado a administrador');
    } catch (error) {
      toast.error('Error al restaurar administrador');
    } finally {
      setIsSwitching(false);
    }
  };

  // Verificar si está en modo switch
  const isSwitchedUser = localStorage.getItem('eureka_original_admin') !== null;

  // Verificar si el usuario actual es admin (original o restaurado)
  const isAdmin = user?.role === 'admin' || isSwitchedUser;

  const availableUsers = [
    { email: 'agente@rdgarantia.com', role: 'agente_comercial', nombre: 'Carlos Agente' },
    { email: 'vendedor@rdgarantia.com', role: 'vendedor', nombre: 'María Vendedor' },
    { email: 'tecnico@rdgarantia.com', role: 'tecnico', nombre: 'Juan Técnico' },
    { email: 'cliente@rdgarantia.com', role: 'cliente', nombre: 'Juan Pérez García' }
  ];

  const tabs = [
    { id: 'usuarios', label: 'Usuarios', icon: Users },
    { id: 'auditoria', label: 'Auditoría', icon: Activity },
    { id: 'permisos', label: 'Permisos', icon: Shield }
  ];

  const usuariosColumns = [
    {
      header: 'Nombre',
      accessor: 'nombre'
    },
    {
      header: 'Email',
      accessor: 'email'
    },
    {
      header: 'Rol',
      cell: (row) => {
        const roleColors = {
          'administrador': 'danger',
          'agente_comercial': 'primary',
          'vendedor': 'success',
          'tecnico': 'warning'
        };
        return <Badge variant={roleColors[row.role]}>{row.role.replace('_', ' ').toUpperCase()}</Badge>;
      }
    },
    {
      header: 'Estado',
      cell: (row) => (
        <Badge variant={row.activo ? 'success' : 'default'}>
          {row.activo ? 'ACTIVO' : 'INACTIVO'}
        </Badge>
      )
    },
    {
      header: 'Último Acceso',
      cell: (row) => row.ultimoAcceso ? formatDateTime(row.ultimoAcceso) : 'Nunca'
    },
    {
      header: 'Acciones',
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            icon={Edit}
            onClick={(e) => {
              e.stopPropagation();
              setEditingUser(row);
              setShowUserModal(true);
            }}
          >
            Editar
          </Button>
          <Button
            variant="danger"
            size="sm"
            icon={Trash2}
            onClick={(e) => {
              e.stopPropagation();
              toast.success('Usuario eliminado');
            }}
          >
            Eliminar
          </Button>
        </div>
      )
    }
  ];

  const auditoriaColumns = [
    {
      header: 'Fecha/Hora',
      cell: (row) => formatDateTime(row.timestamp)
    },
    {
      header: 'Usuario',
      accessor: 'usuario'
    },
    {
      header: 'Acción',
      cell: (row) => (
        <Badge variant="info">{row.accion.replace('_', ' ').toUpperCase()}</Badge>
      )
    },
    {
      header: 'Entidad',
      accessor: 'entidad'
    },
    {
      header: 'Detalles',
      accessor: 'detalles'
    }
  ];

  return (
    <Layout>
      {/* Header con controles de Switch User */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
            <p className="text-gray-600">Gestión completa del sistema Eureka</p>
          </div>

          {/* Controles de Switch User */}
          {isAdmin && (
            <div className="flex items-center space-x-3">
              {isSwitchedUser ? (
                <>
                  <div className="flex items-center space-x-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <RefreshCw className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm text-yellow-800">
                      Modo: {user?.role.replace('_', ' ')} • {user?.nombre}
                    </span>
                  </div>
                  <Button
                    variant="warning"
                    size="sm"
                    icon={LogOut}
                    onClick={handleRestoreAdmin}
                    disabled={isSwitching}
                  >
                    {isSwitching ? 'Restaurando...' : 'Volver Admin'}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  icon={RefreshCw}
                  onClick={() => setShowSwitchModal(true)}
                >
                  Cambiar Rol
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Indicador de estado */}
        {isSwitchedUser && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <RefreshCw className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Modo Switch User Activado
                </h3>
                <div className="mt-1 text-sm text-blue-700">
                  <p>Estás viendo el sistema como <strong>{user?.role.replace('_', ' ')}</strong>.
                  Usa el botón "Volver Admin" para regresar a tu rol de administrador.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-6">
        <Card>
          <div className="border-b border-gray-200 mb-6">
            <div className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`pb-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab === 'usuarios' && (
            <Table
              columns={usuariosColumns}
              data={mockData?.usuarios || []}
            />
          )}

          {activeTab === 'auditoria' && (
            <Table
              columns={auditoriaColumns}
              data={mockData?.auditoria || []}
            />
          )}

          {activeTab === 'permisos' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { role: 'Administrador', permisos: ['Todos los permisos', 'Gestión de usuarios', 'Configuración del sistema'] },
                  { role: 'Agente Comercial', permisos: ['Ver todas las garantías', 'Gestionar averías', 'Generar facturas'] },
                  { role: 'Vendedor', permisos: ['Crear garantías', 'Ver sus propias garantías', 'Ver estadísticas'] },
                  { role: 'Técnico', permisos: ['Ver averías', 'Actualizar estado de averías', 'Subir documentos'] }
                ].map((item, index) => (
                  <Card key={index} title={item.role}>
                    <ul className="space-y-2">
                      {item.permisos.map((permiso, i) => (
                        <li key={i} className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                          <span className="text-sm text-gray-700">{permiso}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Modal de Switch User */}
      <Modal
        isOpen={showSwitchModal}
        onClose={() => setShowSwitchModal(false)}
        title="Cambiar de Rol"
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowSwitchModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => selectedRole && handleSwitchUser(availableUsers.find(u => u.role === selectedRole))}
              disabled={!selectedRole || isSwitching}
            >
              {isSwitching ? 'Cambiando...' : 'Cambiar Rol'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Selecciona el rol al que deseas cambiar. Podrás ver el sistema exactamente como lo vería ese usuario.
          </p>

          <div className="space-y-3">
            {availableUsers.map((userOption) => (
              <div key={userOption.role} className="flex items-center">
                <input
                  id={userOption.role}
                  name="role"
                  type="radio"
                  value={userOption.role}
                  checked={selectedRole === userOption.role}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor={userOption.role} className="ml-3 flex items-center cursor-pointer">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      userOption.role === 'agente_comercial' ? 'bg-blue-500' :
                      userOption.role === 'vendedor' ? 'bg-green-500' :
                      userOption.role === 'tecnico' ? 'bg-yellow-500' :
                      'bg-purple-500'
                    }`}></div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {userOption.role.replace('_', ' ').toUpperCase()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {userOption.nombre} • {userOption.email}
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Importante</h3>
                <div className="mt-1 text-sm text-yellow-700">
                  <p>Al cambiar de rol, verás exactamente lo que vería ese usuario.
                  Usa el botón "Volver Admin" para regresar a tu rol de administrador.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowUserModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                toast.success(editingUser ? 'Usuario actualizado' : 'Usuario creado');
                setShowUserModal(false);
              }}
            >
              {editingUser ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Nombre"
            defaultValue={editingUser?.nombre}
            required
          />
          <Input
            label="Email"
            type="email"
            defaultValue={editingUser?.email}
            required
          />
          <Select
            label="Rol"
            defaultValue={editingUser?.role}
            options={[
              { value: 'administrador', label: 'Administrador' },
              { value: 'agente_comercial', label: 'Agente Comercial' },
              { value: 'vendedor', label: 'Vendedor' },
              { value: 'tecnico', label: 'Técnico' }
            ]}
            required
          />
          <Input
            label="Contraseña"
            type="password"
            placeholder={editingUser ? 'Dejar en blanco para no cambiar' : ''}
            required={!editingUser}
          />
        </div>
      </Modal>
    </Layout>
  );
};

export default AdminPage;
